/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { RefObject } from 'react'
import { Text, Radiobutton, Checkbox } from '@components/atoms'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import { Select, SelectProps, SelectChangeEvent, MenuItem } from '@mui/material'
import { grey } from '@mui/material/colors'

type PropsType = SelectProps & {
	selectRef?: RefObject<any>
	data: { title: string; value: string }[]
	value?: string[]
	label?: string
	className?: string
	onSelectChange?: (values: string[]) => void
	isError?: boolean
	isNaturalEventValue?: boolean
	isMutipleSelection?: boolean
	onOpen?: () => void
	style?: React.CSSProperties
	disabled?: boolean
}

export const Dropdown = (props: PropsType) => {
	// state
	const [value, setValue] = React.useState<string[]>([])
	const [event, setEvent] = React.useState<any>(undefined)
	const [isOpen, setIsOpen] = React.useState<boolean>(false)

	// variables
	const isError: boolean = props.isError ?? false
	const isNaturalEventValue: boolean = props.isNaturalEventValue ?? false
	const isMutipleSelection: boolean = props.isMutipleSelection ?? true

	React.useEffect(() => {
		if (props.onSelectChange !== undefined) {
			if (isNaturalEventValue) {
				props.onSelectChange(event)
			} else {
				if (value[0] !== undefined) {
					props.onSelectChange(value)
				}
			}
		}
	}, [value, event])

	React.useEffect(() => {
		if (props.value !== undefined) {
			const idx = props.data.findIndex(aData => props.value?.includes(aData.value))

			if (idx === -1) {
				setValue([])
			} else {
				setValue([...props.value])
			}
		}
	}, [props.value])

	const onOpen = React.useCallback(() => {
		setIsOpen(true)

		if (props.onOpen) {
			props.onOpen()
		}
	}, [props.onOpen])

	const onClose = React.useCallback(() => {
		setIsOpen(false)
	}, [])

	const onChange = React.useCallback(
		(e: SelectChangeEvent<typeof value>) => {
			if (isOpen) {
				if (isNaturalEventValue) {
					setEvent(e)
				}

				const tempValue = e.target.value
				const newValue: string[] =
					typeof tempValue === 'string' && isMutipleSelection
						? tempValue.split(',')
						: typeof tempValue === 'string'
						? [tempValue]
						: tempValue

				setValue(newValue)
			}
		},
		[isNaturalEventValue, isMutipleSelection, isOpen],
	)

	return (
		<FormControl
			sx={{ m: 1 }}
			size="small"
			css={style}
			className={props.className}
			error={isError}
			disabled={props.disabled}
		>
			{props.label ? <InputLabel id="dropdownComponant">{props.label}</InputLabel> : null}

			<Select
				inputRef={props.selectRef ?? null}
				sx={{ ...props.sx }}
				style={{ ...props.style }}
				labelId="dropdownComponant"
				id="dropdownComponant"
				className="dropdownComponant"
				onChange={onChange}
				fullWidth={props.fullWidth ?? true}
				multiple={isMutipleSelection}
				value={
					value.length > 0 ? value : value.length == 0 && isMutipleSelection ? [] : ''
				}
				label={props.label ?? ''}
				size={props.size ?? 'small'}
				onOpen={onOpen}
				onClose={onClose}
				variant={props.variant ?? 'outlined'}
				displayEmpty
				inputProps={props.label ? {} : { 'aria-label': 'Without label' }}
				renderValue={selected => {
					const newSelectedTitleArr =
						typeof selected === 'string'
							? []
							: selected.map(aSelected => {
									const selectedData = props.data.filter(
										aData => aData.value === aSelected,
									)
									return selectedData.length > 0 ? selectedData[0]['title'] : ''
							  })
					return newSelectedTitleArr.length > 0 ? newSelectedTitleArr.join(', ') : ''
				}}
			>
				{props.data.map(aData => {
					const isChecked: boolean = isMutipleSelection
						? value.indexOf(aData.value) > -1
						: value.length > 0
						? value.includes(aData.value) && aData.value !== ''
						: value.length === 0
						? aData.value === ''
						: false

					return (
						<MenuItem
							key={aData.value}
							sx={{
								'&.Mui-focusVisible': {
									background: grey[200],
								},
							}}
							value={aData.value}
						>
							{isMutipleSelection ? (
								<Checkbox checked={isChecked} />
							) : (
								<Radiobutton
									value={aData.value}
									selectedValue={isChecked ? aData.value : '(NONE)'}
								/>
							)}
							<Text category="p1" css={{ paddingRight: '0.8rem' }}>
								{aData.title}
							</Text>
						</MenuItem>
					)
				})}
			</Select>
		</FormControl>
	)
}

const style = css({
	minWidth: '100%',
	margin: 0,

	'.dropdownComponant': {
		height: '2.4rem',
		fontSize: '12px',
	},
})
